import React, { useState, useEffect } from 'react';
import getThemeLookup from '../selectors/getThemeLookup';
import { useAppSelector } from '../app/hooks';
import { CONTAINER_PROPERTIES } from '../utils/theme';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';

interface MFACodeInputProps {
  placeholder?: string;
  containerStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  onChange?: (values: string[]) => void;
  resetMFAInput?: boolean;
}

const MFACodeInput: React.FC<MFACodeInputProps> = ({
  placeholder = '',
  containerStyle,
  inputStyle,
  onChange,
  resetMFAInput,
}) => {
  const [values, setValues] = useState(Array(6).fill(''));
  const p = useAppSelector(getThemeLookup);
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);

  useEffect(() => {
    if (resetMFAInput) {
      setValues(Array(6).fill(''));
    }
  }, [resetMFAInput]);

  const handleChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const newValues = [...values];
    newValues[index] = event.target.value;
    setValues(newValues);

    if (onChange) {
      onChange(newValues);
    }

    // Move focus to the next input box if the current one is filled
    if (event.target.value && index < 5) {
      const nextSibling = document.getElementById(`mfa-input-${index + 1}`);
      if (nextSibling) {
        nextSibling.focus();
      }
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowRight' && index < 5) {
      const nextSibling = document.getElementById(`mfa-input-${index + 1}`);
      if (nextSibling) {
        nextSibling.focus();
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      const prevSibling = document.getElementById(`mfa-input-${index - 1}`);
      if (prevSibling) {
        prevSibling.focus();
        // Ensure the cursor is at the end of the input if there is a value
        if (values[index - 1]) {
          setTimeout(() => {
            (prevSibling as HTMLInputElement).setSelectionRange(
              values[index - 1].length,
              values[index - 1].length,
            );
          }, 0);
        }
      }
    } else if (event.key === 'Backspace' && index > 0) {
      if (!values[index]) {
        // If current field is empty, clear previous field and move focus there
        const newValues = [...values];
        newValues[index - 1] = '';
        setValues(newValues);
        if (onChange) {
          onChange(newValues);
        }
        const prevSibling = document.getElementById(`mfa-input-${index - 1}`);
        if (prevSibling) {
          prevSibling.focus();
        }
      }
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault(); // Prevent default paste behavior
    const pasteData = event.clipboardData.getData('Text').slice(0, 6);
    const newValues = pasteData.split('');
    setValues(newValues);

    // Ensure onChange is called with the new values
    if (onChange) {
      onChange(newValues);
    }

    // If we have a complete code (6 digits), focus the last input
    if (newValues.length === 6 && newValues.every(value => value !== '')) {
      const lastInput = document.getElementById('mfa-input-5');
      if (lastInput) {
        lastInput.focus();
        // Set cursor position to end of input
        setTimeout(() => {
          (lastInput as HTMLInputElement).setSelectionRange(1, 1);
        }, 0);
      }
    } else {
      // Otherwise focus the next empty input
      const nextIndex = newValues.findIndex(value => value === '');
      const nextSibling = document.getElementById(`mfa-input-${nextIndex}`);
      if (nextSibling) {
        nextSibling.focus();
      }
    }
  };

  return (
    <div style={{ display: 'flex', ...containerStyle, gap: deviceTypeMobile ? '4vw' : '28px' }}>
      {values.map((value, index) => (
        <input
          key={index}
          id={`mfa-input-${index}`}
          type="text"
          maxLength={1}
          value={value}
          placeholder={placeholder}
          onChange={e => handleChange(index, e)}
          onKeyDown={e => handleKeyDown(index, e)}
          onPaste={handlePaste}
          style={{
            width: deviceTypeMobile ? '10vw' : '40px',
            height: deviceTypeMobile ? '60px' : '60px',
            textAlign: 'center',
            cursor: 'text',
            fontSize: deviceTypeMobile ? '24px' : '40px',
            borderRadius: deviceTypeMobile ? '8px' : '0px',
            ...(!deviceTypeMobile && {
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
            }),
            ...inputStyle,
            ...(value ? p('mfaCodeInput', [...CONTAINER_PROPERTIES, 'border']) : {}),
          }}
          onFocus={e => {
            const length = e.target.value.length;
            e.target.setSelectionRange(length, length);
          }}
        />
      ))}
    </div>
  );
};

export default MFACodeInput;
